import {
  ICanCloseConciliation,
  IDemonstrativeExportQueued,
  IOneStoreConciliation,
  IOpenCashRegistersResponse,
  IPartialOperatorBalanceResponse,
} from "src/@types/store-conciliation";
import { Tags } from "../enum/tags";
import { apiSlice } from "./http";
import { Paginated } from "src/@types/paginated";
import { IGetStoresParams } from "src/@types/stores";

export const storeConciliationInformations = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getOneStoreConciliation: builder.query<
      IOneStoreConciliation,
      { idTransaction: string }
    >({
      query: ({ idTransaction }) => {
        return {
          url: `/store-conciliation/${idTransaction}`,
          method: "GET",
        };
      },
      providesTags: [Tags.OPTIONS_REGIONAL],
    }),
    getStoresConciliations: builder.query<
      Paginated<IOneStoreConciliation>,
      IGetStoresParams
    >({
      query: (filters) => {
        const params = new URLSearchParams(
          filters as unknown as Record<string, string>
        );

        return {
          url: `/store-conciliation?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.STORE_CONCILIATION],
    }),
    canCloseConciliation: builder.query<ICanCloseConciliation, string>({
      query: (id) => {
        return {
          url: `/store-conciliation/can-close/${id}`,
          method: "GET",
        };
      },
      providesTags: [Tags.CAN_CLOSE_CONCILIATION],
    }),
    closeConciliation: builder.mutation<IOneStoreConciliation, string>({
      query: (id) => {
        return {
          url: `/store-conciliation/close/${id}`,
          method: "PUT",
        };
      },
      invalidatesTags: [Tags.STORE, Tags.STORE_CONCILIATION, Tags.CONCILIATION_HISTORY],
    }),
    openConciliation: builder.mutation<IOneStoreConciliation, string>({
      query: (id) => {
        return {
          url: `/store-conciliation/open/${id}`,
          method: "PUT",
        };
      },
      invalidatesTags: [Tags.STORE, Tags.STORE_CONCILIATION],
    }),
    reprocessConciliation: builder.mutation<
      { message: string; conciliation_id: number },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/store-conciliation/reprocess/${id}`,
        method: "POST",
      }),
      invalidatesTags: [Tags.STORE_CONCILIATION, Tags.CONCILIATION_HISTORY],
    }),
    reprocessConciliationAsync: builder.mutation<
      { message: string; job_id: string; conciliation_id: number },
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/store-conciliation/reprocess-async/${id}`,
        method: "POST",
      }),
      invalidatesTags: [Tags.STORE_CONCILIATION, Tags.CONCILIATION_HISTORY],
    }),
    reprocessNsu: builder.mutation({
      query: (conciliation_id) => ({
        url: `/nsu/reprocess/${conciliation_id}`,
        method: "POST",
      }),
      invalidatesTags: [Tags.STORE_CONCILIATION, Tags.NSU, Tags.CONCILIATION_HISTORY],
    }),
    getCronJobForStore: builder.query<
      void,
      { lambda?: boolean; daysBack: number }
    >({
      query: ({ lambda, daysBack }) => {
        const params = new URLSearchParams();
        if (lambda !== undefined) {
          params.append("lambda", lambda.toString());
        }

        return {
          url: `/store/job/load/${daysBack}?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.STORE_CONCILIATION],
    }),

    reprocessAllStores: builder.mutation<
      { jobId: string | null; message: string },
      { daysBack?: number; date?: string; reprocessAll: boolean; onlyUnlisted?: boolean }
    >({
      query: ({ daysBack, date, reprocessAll, onlyUnlisted }) => ({
        url: `/store/run-job/`,
        method: "POST",
        body: { daysBack, date, reprocessAll, onlyUnlisted },
      }),
      invalidatesTags: [Tags.STORE_CONCILIATION, Tags.CONCILIATION_HISTORY],
    }),

    retryFailedStores: builder.mutation<{ jobId: string; storeCount: number }, void>({
      query: () => ({
        url: `/store/job/retry-failed`,
        method: "POST",
      }),
      invalidatesTags: [Tags.STORE_CONCILIATION, Tags.CONCILIATION_HISTORY],
    }),

    exportStoresConciliationsExcel: builder.mutation<string, IGetStoresParams>({
      query: (filters) => {
        const params = new URLSearchParams();
        
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(v => params.append(`${key}[]`, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });

        return {
          url: `/store-conciliation/export/excel?${params.toString()}`,
          method: "GET",
          responseHandler: async (response: Response): Promise<string> => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const jsonResponse = await response.json();
            return jsonResponse.excelFile;
          },
        };
      },
    }),

    exportStoresConciliationsPdf: builder.mutation<string, IGetStoresParams>({
      query: (filters) => {
        const params = new URLSearchParams();
        
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            if (Array.isArray(value)) {
              value.forEach(v => params.append(`${key}[]`, v.toString()));
            } else {
              params.append(key, value.toString());
            }
          }
        });

        return {
          url: `/store-conciliation/export/pdf?${params.toString()}`,
          method: "GET",
          responseHandler: async (response: Response): Promise<string> => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            const jsonResponse = await response.json();
            return jsonResponse.pdfFile;
          },
        };
      },
    }),
    getPartialOperatorBalances: builder.query<
      IPartialOperatorBalanceResponse,
      { conciliationId: string }
    >({
      query: ({ conciliationId }) => ({
        url: `/store-conciliation/${conciliationId}/partial-operators`,
        method: "GET",
      }),
      providesTags: [Tags.STORE_CONCILIATION],
    }),

    getOpenCashRegisters: builder.query<
      IOpenCashRegistersResponse,
      { conciliationId: string }
    >({
      query: ({ conciliationId }) => ({
        url: `/store-conciliation/${conciliationId}/open-cash-registers`,
        method: "GET",
      }),
      providesTags: [Tags.STORE_CONCILIATION],
    }),

    exportConciliationDemonstrativeExcel: builder.mutation<
      IDemonstrativeExportQueued,
      { conciliationId: string }
    >({
      query: ({ conciliationId }) => ({
        url: `/store-conciliation/${conciliationId}/export/excel`,
        method: "POST",
      }),
    }),

    exportConciliationDemonstrativePdf: builder.mutation<
      IDemonstrativeExportQueued,
      { conciliationId: string }
    >({
      query: ({ conciliationId }) => ({
        url: `/store-conciliation/${conciliationId}/export/pdf`,
        method: "POST",
      }),
    }),

    getReprocessStatus: builder.query<
      { isProcessing: boolean; job?: any },
      { conciliationId: number }
    >({
      query: ({ conciliationId }) => ({
        url: `/store-conciliation/reprocess-status/${conciliationId}`,
        method: "GET",
      }),
    }),
    getOpenConciliationsForStore: builder.query<any, { storeCode: number, conciliationDate: string }>({
      query: ({ storeCode, conciliationDate }) => ({
        url: `/store-conciliation/open/${storeCode}?conciliationDate=${conciliationDate}`,
        method: "GET",
      }),
      providesTags: [Tags.STORE_CONCILIATION],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetOneStoreConciliationQuery,
  useGetStoresConciliationsQuery,
  useCanCloseConciliationQuery,
  useCloseConciliationMutation,
  useOpenConciliationMutation,
  useReprocessConciliationMutation,
  useReprocessConciliationAsyncMutation,
  useGetReprocessStatusQuery,
  useReprocessNsuMutation,
  useLazyGetCronJobForStoreQuery,
  useReprocessAllStoresMutation,
  useRetryFailedStoresMutation,
  useGetOpenConciliationsForStoreQuery,
  useExportStoresConciliationsExcelMutation,
  useExportStoresConciliationsPdfMutation,
  useGetPartialOperatorBalancesQuery,
  useGetOpenCashRegistersQuery,
  useExportConciliationDemonstrativeExcelMutation,
  useExportConciliationDemonstrativePdfMutation,
} = storeConciliationInformations;

export const {
  endpoints: {
    getOneStoreConciliation: { select: selectGetOneStoreConciliation },
    getStoresConciliations: { select: selectGetStoresConciliations },
    canCloseConciliation: { select: selectCanCloseConciliation },
    openConciliation: { select: selectOpenConciliation },
    closeConciliation: { select: selectCloseConciliation },
    getCronJobForStore: { select: selectGetCronJobForStore },
    reprocessConciliation: { select: selectReprocessConciliation },
    reprocessNsu: { select: selectReprocessNsu },
  },
} = storeConciliationInformations;
